import { useState, useEffect } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import Button from 'components/base/Button';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { addAsset, AssetDataType, CreateAssetPayload } from 'store/assetsSlice';
import {
  getCategories,
  Category,
  createCategory,
  assignAssetToUser
} from 'api/assetsAPI';
import { getUsers, User } from 'api/userAPI';
import { getCompanies, Company } from 'api/companyAPI';
import { getEffectiveCompanyContext } from 'api/contextAPI';
import { parseApiError } from 'utils/apiErrorParser';

interface CreateAssetModalProps {
  show: boolean;
  handleClose: () => void;
}

interface ApiError {
  response?: {
    status?: number;
    data?: {
      detail?: string | Array<{ msg?: string; message?: string }>;
      message?: string;
    };
  };
  message?: string;
}

const CreateAssetModal = ({ show, handleClose }: CreateAssetModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading: reduxLoading, error } = useSelector(
    (state: RootState) => state.assets
  );
  const { companyId, role } = useSelector((state: RootState) => state.auth);

  const [categories, setCategories] = useState<Category[]>([]);
  const [formError, setFormError] = useState<string>('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  // States for Super Admin
  const [companies, setCompanies] = useState<Company[]>([]);

  // NEW: Single loading state for company data
  const [companyDataLoading, setCompanyDataLoading] = useState(false);

  // Step 2: Assignment after creation
  const [showAssignmentStep, setShowAssignmentStep] = useState(false);
  const [createdAssetId, setCreatedAssetId] = useState<number | null>(null);
  const [createdAssetName, setCreatedAssetName] = useState<string>('');
  const [assignmentCompanyId, setAssignmentCompanyId] = useState<number>(0);
  const [assignmentUserId, setAssignmentUserId] = useState<number>(0);
  const [assignmentUsers, setAssignmentUsers] = useState<User[]>([]);
  const [loadingAssignmentUsers, setLoadingAssignmentUsers] = useState(false);
  const [assigningUser, setAssigningUser] = useState(false);
  const [assignmentError, setAssignmentError] = useState<string>('');

  const userCompanyId = companyId ? parseInt(companyId) : null;
  const isSuperAdmin = role === 'super_user';
  const canCreateAssets = isSuperAdmin || userCompanyId;

  const [effectiveCompanyId, setEffectiveCompanyId] = useState<number | null>(
    null
  );

  const [formData, setFormData] = useState<
    Omit<
      AssetDataType,
      | 'id'
      | 'created_at'
      | 'updated_at'
      | 'category_name'
      | 'current_holder_name'
      | 'company_name'
    >
  >({
    name: '',
    asset_tag: '',
    serial_number: '',
    brand: '',
    model: '',
    description: '',
    status: 'available',
    condition: 'excellent',
    purchase_date: new Date().toISOString().split('T')[0],
    purchase_cost: 0,
    purchase_invoice_number: '',
    vendor: '',
    warranty_expiry_date: '',
    location: '',
    company_id: isSuperAdmin ? 0 : userCompanyId || 0,
    category_id: 0,
    current_holder_id: 0
  });

  // Fetch effective company from context API
  useEffect(() => {
    const fetchEffectiveCompany = async () => {
      try {
        // Replace axios.get with getEffectiveCompanyContext
        const context = await getEffectiveCompanyContext();
        setEffectiveCompanyId(context.company_id || null);
      } catch (error) {
        console.error('Failed to fetch effective company:', error);
        setEffectiveCompanyId(userCompanyId);
      }
    };

    if (show) {
      fetchEffectiveCompany();
      if (isSuperAdmin) {
        loadCompanies();
      }
      resetForm();
    }
  }, [show]);

  // Load categories for the selected company
  const loadCompanyData = async (companyId: number) => {
    if (!companyId) return;

    setCompanyDataLoading(true);
    try {
      const categoriesData = await getCategories(companyId);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Failed to load company data:', error);
      setCategories([]);
      setFormError('Failed to load categories. Please try again.');
    } finally {
      setCompanyDataLoading(false);
    }
  };

  // Load companies for Super Admin
  const loadCompanies = async () => {
    try {
      const companiesData = await getCompanies();
      setCompanies(companiesData);
    } catch (error) {
      console.error('Failed to load companies:', error);
      setCompanies([]);
    }
  };

  const resetForm = () => {
    const defaultCompanyId = isSuperAdmin
      ? 0
      : effectiveCompanyId || userCompanyId || 0;

    setFormData({
      name: '',
      asset_tag: '',
      serial_number: '',
      brand: '',
      model: '',
      description: '',
      status: 'available',
      condition: 'excellent',
      purchase_date: new Date().toISOString().split('T')[0],
      purchase_cost: 0,
      purchase_invoice_number: '',
      vendor: '',
      warranty_expiry_date: '',
      location: '',
      company_id: defaultCompanyId,
      category_id: 0,
      current_holder_id: 0
    });
    setFormError('');
    setNewCategoryName('');
    setShowCategoryModal(false);
    setCategories([]);
    setCompanyDataLoading(false);

    // Reset assignment step
    setShowAssignmentStep(false);
    setCreatedAssetId(null);
    setCreatedAssetName('');
    setAssignmentCompanyId(0);
    setAssignmentUserId(0);
    setAssignmentUsers([]);
    setAssignmentError('');
  };

  // Load users for assignment
  const loadAssignmentUsers = async (companyId: number) => {
    if (!companyId) return;

    setLoadingAssignmentUsers(true);
    try {
      const usersData = await getUsers({ company_id: companyId });
      setAssignmentUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
      setAssignmentUsers([]);
      setAssignmentError('Failed to load users. Please try again.');
    } finally {
      setLoadingAssignmentUsers(false);
    }
  };

  // Handle assignment company change (Super Admin only)
  const handleAssignmentCompanyChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const companyId = Number(e.target.value);
    setAssignmentCompanyId(companyId);
    setAssignmentUserId(0);
    setAssignmentUsers([]);

    if (companyId > 0) {
      await loadAssignmentUsers(companyId);
    }
  };

  // Handle assignment submission
  const handleAssignAsset = async () => {
    if (!createdAssetId || !assignmentUserId) {
      setAssignmentError('Please select a user to assign.');
      return;
    }

    setAssigningUser(true);
    setAssignmentError('');

    try {
      await assignAssetToUser(createdAssetId, {
        user_id: assignmentUserId,
        condition_at_assignment: formData.condition
      });

      // Success - close modal and refresh
      handleClose();
    } catch (error: unknown) {
      console.error('Failed to assign asset:', error);
      const errorMessage = parseApiError(
        error,
        'Failed to assign asset. Please try again.'
      );
      setAssignmentError(errorMessage);
    } finally {
      setAssigningUser(false);
    }
  };

  // Skip assignment and close
  const handleSkipAssignment = () => {
    handleClose();
  };

  // NEW: Consolidated company change handler
  const handleCompanyChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const companyId = Number(e.target.value);

    // Update form data immediately
    setFormData(prev => ({
      ...prev,
      company_id: companyId,
      category_id: 0,
      current_holder_id: 0
    }));

    // Reset dependent selections
    setCategories([]);

    // Load categories if a company is selected
    if (companyId > 0) {
      await loadCompanyData(companyId);
    }
  };

  // Create new category
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      setFormError('Category name is required');
      return;
    }

    const existingCategory = categories.find(
      cat => cat.name.toLowerCase() === newCategoryName.trim().toLowerCase()
    );

    if (existingCategory) {
      setFormError(`Category "${newCategoryName}" already exists`);
      return;
    }

    setCreatingCategory(true);
    try {
      const selectedCompanyId = isSuperAdmin
        ? formData.company_id
        : effectiveCompanyId || userCompanyId;
      const newCategory = await createCategory({
        name: newCategoryName.trim(),
        description: '',
        company_id: selectedCompanyId || 0
      });

      // Reload categories to include the new one
      if (selectedCompanyId) {
        const updatedCategories = await getCategories(selectedCompanyId);
        setCategories(updatedCategories);
      }

      // Auto-select the new category
      setFormData(prev => ({ ...prev, category_id: newCategory.id }));
      setNewCategoryName('');
      setShowCategoryModal(false);
      setFormError('');
    } catch (error: unknown) {
      console.error('Failed to create category:', error);
      const apiError = error as ApiError;
      setFormError(
        apiError?.response?.data?.message || 'Failed to create category'
      );
    } finally {
      setCreatingCategory(false);
    }
  };

  // Form validation
  const validateForm = (): string | null => {
    // Basic required fields
    const requiredFields = [
      { field: formData.name.trim(), name: 'Device Name' },
      { field: formData.asset_tag.trim(), name: 'Asset Tag' },
      { field: formData.serial_number.trim(), name: 'Serial Number' },
      { field: formData.brand.trim(), name: 'Brand' },
      { field: formData.model.trim(), name: 'Model' },
      { field: formData.description.trim(), name: 'Description' },
      {
        field: formData.purchase_invoice_number.trim(),
        name: 'Purchase Invoice Number'
      },
      { field: formData.vendor.trim(), name: 'Vendor' },
      { field: formData.location.trim(), name: 'Location' },
      { field: formData.purchase_date.trim(), name: 'Purchase Date' },
      {
        field: formData.warranty_expiry_date.trim(),
        name: 'Warranty Expiry Date'
      }
    ];

    const missingFields = requiredFields.filter(field => !field.field);
    if (missingFields.length > 0) {
      return `Required fields missing: ${missingFields
        .map(f => f.name)
        .join(', ')}`;
    }

    // Super Admin specific validations
    if (isSuperAdmin) {
      if (formData.company_id === 0) {
        return 'Please select a company';
      }
      if (companyDataLoading) {
        return 'Please wait for company data to finish loading';
      }
    }

    // Category validation for everyone
    if (formData.category_id === 0) {
      return 'Please select a category';
    }

    // Check if categories are loaded but empty
    if (
      !companyDataLoading &&
      categories.length === 0 &&
      formData.company_id > 0
    ) {
      return 'No categories found for selected company. Please create one first.';
    }

    return null; // No errors
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Run validation
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    if (!canCreateAssets) {
      setFormError(
        'You do not have permission to create assets. Please contact administrator.'
      );
      return;
    }

    try {
      const finalCompanyId = isSuperAdmin
        ? formData.company_id
        : effectiveCompanyId || userCompanyId;

      const payload: CreateAssetPayload = {
        assetData: {
          ...formData,
          company_id: finalCompanyId || 0,
          category_id: formData.category_id,
          purchase_cost: Number(formData.purchase_cost) || 0
        },
        userCompanyId: finalCompanyId || 0,
        categoryId: formData.category_id,
        currentHolderId: formData.current_holder_id
      };

      const newAsset = await dispatch(addAsset(payload)).unwrap();

      // Show success step with assignment option
      setCreatedAssetId(newAsset.id);
      setCreatedAssetName(newAsset.name);
      setShowAssignmentStep(true);

      // For admin, pre-set assignment company
      if (!isSuperAdmin && finalCompanyId) {
        setAssignmentCompanyId(finalCompanyId);
        loadAssignmentUsers(finalCompanyId);
      }
    } catch (error: unknown) {
      console.error('Failed to create asset:', error);
      let errorMessage = 'Failed to create asset. Please try again.';
      const apiError = error as ApiError;

      if (apiError?.response?.status === 422) {
        const validationErrors = apiError.response.data?.detail;
        if (Array.isArray(validationErrors)) {
          errorMessage =
            'Validation errors: ' +
            validationErrors.map(err => err.msg || err.message).join(', ');
        } else if (typeof validationErrors === 'string') {
          errorMessage = validationErrors;
        } else {
          errorMessage =
            'Validation error: Please check all required fields and data formats.';
        }
      } else if (apiError?.message) {
        errorMessage = apiError.message;
      }

      setFormError(errorMessage);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    let processedValue: string | number = value;

    if (name === 'purchase_cost') {
      processedValue = value === '' ? 0 : Number(value);
    } else if (name === 'category_id' || name === 'current_holder_id') {
      processedValue = Number(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    if (formError) setFormError('');
  };

  const handleCloseModal = () => {
    setFormError('');
    handleClose();
  };

  const openCategoryModal = () => {
    setNewCategoryName('');
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setNewCategoryName('');
  };

  // Determine if category dropdown should be disabled
  const isCategoryDisabled = () => {
    if (isSuperAdmin) {
      return companyDataLoading || formData.company_id === 0;
    }
    return companyDataLoading;
  };

  return (
    <>
      {/* Main Asset Creation Modal */}
      <Modal show={show} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {showAssignmentStep
              ? 'Asset Created Successfully!'
              : 'Create New Asset'}
          </Modal.Title>
        </Modal.Header>

        {!showAssignmentStep ? (
          // Step 1: Create Asset Form
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {(formError || error) && (
                <div className="alert alert-danger mb-3">
                  {formError || error}
                </div>
              )}

              <Row className="g-3">
                {/* Company Selector - Only for Super Admin */}
                {isSuperAdmin && (
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Company *</Form.Label>
                      <Form.Select
                        name="company_id"
                        value={formData.company_id}
                        onChange={handleCompanyChange}
                        required
                        disabled={companyDataLoading}
                      >
                        <option value={0}>Select Company *</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </Form.Select>
                      {companyDataLoading && formData.company_id > 0 && (
                        <Form.Text className="text-muted">
                          Loading company data...
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                )}

                {/* Device Details */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Device Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter device name"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Asset Tag *</Form.Label>
                    <Form.Control
                      type="text"
                      name="asset_tag"
                      value={formData.asset_tag}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter asset tag"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Serial Number *</Form.Label>
                    <Form.Control
                      type="text"
                      name="serial_number"
                      value={formData.serial_number}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter serial number"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Brand *</Form.Label>
                    <Form.Control
                      type="text"
                      name="brand"
                      value={formData.brand}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter brand"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Model *</Form.Label>
                    <Form.Control
                      type="text"
                      name="model"
                      value={formData.model}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter model"
                    />
                  </Form.Group>
                </Col>

                {/* Category - NOW WORKS INDEPENDENTLY OF DEPARTMENT */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Category *</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        disabled={isCategoryDisabled()}
                        style={{ flex: 1 }}
                        required
                      >
                        <option value={0}>
                          {companyDataLoading
                            ? 'Loading categories...'
                            : isSuperAdmin && formData.company_id === 0
                            ? 'Select company first'
                            : categories.length === 0
                            ? 'No categories found'
                            : 'Select Category *'}
                        </option>
                        {categories.map(category => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </Form.Select>
                      <Button
                        variant={
                          categories.length === 0
                            ? 'primary'
                            : 'outline-primary'
                        }
                        size="sm"
                        onClick={openCategoryModal}
                        disabled={isCategoryDisabled()}
                        style={{ whiteSpace: 'nowrap' }}
                        title={
                          isCategoryDisabled()
                            ? isSuperAdmin && formData.company_id === 0
                              ? 'Select a company first'
                              : 'Loading...'
                            : 'Add new category'
                        }
                      >
                        {categories.length === 0 ? 'Create Category' : '+ New'}
                      </Button>
                    </div>
                    {!companyDataLoading &&
                      categories.length === 0 &&
                      formData.company_id > 0 && (
                        <Form.Text className="text-danger">
                          No categories found. Please create one first.
                        </Form.Text>
                      )}
                  </Form.Group>
                </Col>

                {/* User Assignment */}

                {/* Purchase Details */}
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Purchase Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="purchase_date"
                      value={formData.purchase_date}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Purchase Invoice Number *</Form.Label>
                    <Form.Control
                      type="text"
                      name="purchase_invoice_number"
                      value={formData.purchase_invoice_number}
                      onChange={handleInputChange}
                      placeholder="Enter invoice number"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Vendor *</Form.Label>
                    <Form.Control
                      type="text"
                      name="vendor"
                      value={formData.vendor}
                      onChange={handleInputChange}
                      placeholder="Enter vendor"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Warranty Expiry Date *</Form.Label>
                    <Form.Control
                      type="date"
                      name="warranty_expiry_date"
                      value={formData.warranty_expiry_date}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Location *</Form.Label>
                    <Form.Control
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="Enter location"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Condition *</Form.Label>
                    <Form.Select
                      name="condition"
                      value={formData.condition}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="excellent">Excellent</option>
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Description *</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      placeholder="Enter asset description"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleCloseModal}
                disabled={reduxLoading}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  reduxLoading ||
                  !canCreateAssets ||
                  companyDataLoading ||
                  (isSuperAdmin && formData.company_id === 0) ||
                  (categories.length === 0 && formData.company_id > 0)
                }
                title={
                  companyDataLoading
                    ? 'Loading company data...'
                    : isSuperAdmin && formData.company_id === 0
                    ? 'Select a company first'
                    : categories.length === 0 && formData.company_id > 0
                    ? 'No categories available for this company'
                    : !canCreateAssets
                    ? "You don't have permission"
                    : undefined
                }
              >
                {reduxLoading || companyDataLoading
                  ? 'Loading...'
                  : 'Create Asset'}
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          // Step 2: Assignment Step
          <>
            <Modal.Body>
              <div className="text-center mb-4">
                <div className="mb-3">
                  <i
                    className="bi bi-check-circle text-success"
                    style={{ fontSize: '3rem' }}
                  ></i>
                </div>
                <h5>
                  Asset "{createdAssetName}" has been created successfully!
                </h5>
                <p className="text-muted">
                  Would you like to assign this asset to a user now?
                </p>
              </div>

              {assignmentError && (
                <div className="alert alert-danger">{assignmentError}</div>
              )}

              <Row className="g-3">
                {/* Super Admin: Select Company First */}
                {isSuperAdmin && (
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>Select Company *</Form.Label>
                      <Form.Select
                        value={assignmentCompanyId}
                        onChange={handleAssignmentCompanyChange}
                        disabled={loadingAssignmentUsers}
                      >
                        <option value={0}>Select Company</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                )}

                {/* Select User */}
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Select User *</Form.Label>
                    <Form.Select
                      value={assignmentUserId}
                      onChange={e =>
                        setAssignmentUserId(Number(e.target.value))
                      }
                      disabled={
                        loadingAssignmentUsers ||
                        (isSuperAdmin && assignmentCompanyId === 0) ||
                        assignmentUsers.length === 0
                      }
                    >
                      <option value={0}>
                        {loadingAssignmentUsers
                          ? 'Loading users...'
                          : isSuperAdmin && assignmentCompanyId === 0
                          ? 'Select company first'
                          : assignmentUsers.length === 0
                          ? 'No users available'
                          : 'Select User'}
                      </option>
                      {assignmentUsers.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={handleSkipAssignment}
                disabled={assigningUser}
              >
                Skip, I'll Assign Later
              </Button>
              <Button
                variant="primary"
                onClick={handleAssignAsset}
                disabled={
                  assigningUser ||
                  assignmentUserId === 0 ||
                  (isSuperAdmin && assignmentCompanyId === 0)
                }
              >
                {assigningUser ? 'Assigning...' : 'Assign to User'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      {/* Create Category Modal */}
      <Modal show={showCategoryModal} onHide={closeCategoryModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Category Name *</Form.Label>
            <Form.Control
              type="text"
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              placeholder="Enter category name"
              required
              onKeyPress={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreateCategory();
                }
              }}
            />
            <Form.Text className="text-muted">
              This category will be saved in the database and available for all
              assets in this company.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={closeCategoryModal}
            disabled={creatingCategory}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateCategory}
            disabled={creatingCategory || !newCategoryName.trim()}
          >
            {creatingCategory ? 'Creating...' : 'Create Category'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateAssetModal;
